import {
  CardList,
  CardListShimmer,
  EntityList,
  Filters,
  matchFilters,
  useInMemoryFilteredList,
  EntityListNavigationType,
  SortDropdown,
  SortDirection,
  ListConductorRawFilter,
  OperandValue,
  Tooltip
} from '@hevoio/hevo-ui-toolkit';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ActivateTargetAPI from '../../../activate-target/ActivateTargetAPI';
import { TargetsFactory } from '../../../activate-target/utils';
import { TARGET_FILTERS, TARGET_SORT_DROPDOWN_OPTIONS } from './constants';
import { Target } from '../../../activate-target/models/target';
import { CreateTargetButton } from './CreateTargetButton';
import { TargetListCardItem } from './TargetListCardItem';
import { RbacPermissions } from '../../../../core/interfaces/user';
import { RBAC_DISABLED_MESSAGE } from '../../../../../react/access-control/constants/strings';
import useHasPermission from '../../../../access-control/hooks/useHasPermission';

const SEARCH_FILTER_KEY = 'term';

export function TargetList() {
  const navigate = useNavigate();
  const { hasPermission } = useHasPermission();

  const { fetch, reset } = useInMemoryFilteredList(
    async () => {
      const res = await ActivateTargetAPI.getTargets();
      return TargetsFactory(res.data);
    },
    (item, params) => {
      return matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterKeyMatchValue(filterKey, item)
      );
    },
    undefined,
    undefined,
    (item, field) => {
      if (field === 'id') {
        return item.id;
      }

      return item.name.trim().toLowerCase();
    },
    true
  );

  const getFilterKeyMatchValue = (filterKey: string, item: Target): OperandValue => {
    switch (filterKey) {
      case 'status':
        return item.status.value;
      case SEARCH_FILTER_KEY:
        return [item.seqId, item.name, item.targetTypeDisplay];
      default:
        return undefined;
    }
  };

  return (
    <EntityList
      entity='activate-target'
      dataId='activate-target-list'
      mode='standalone'
      navigationType={EntityListNavigationType.INFINITE_SCROLL}
      dataSource$={fetch}
      listIcon='target'
      entityTitle=''
      emptyTitle='No Matching Targets Found'
      emptySubTitle='Modify search term to find the desired Targets'
      searchKey={SEARCH_FILTER_KEY}
      showSearch
      showFilters
      inMemoryList
      onRefresh={() => {
        reset();
      }}
      newListConnectedTitle='No Activation Target Created Yet'
      newListConnectedSubTitle='Start by adding the Targets where you want to move your Warehouse data'
      newListComponentProps={{
        primaryCTAProps: {
          dataId: 'create-activation-target',
          icon: 'plus',
          className: 'px-3 py-2',
          disabled: !hasPermission(RbacPermissions.ACTIVATION_CREATE),
          onClick: () => {
            navigate('/target/create');
          },
          size: 'md',
          children: <Tooltip dataId='target-tooltip-btn' title={!hasPermission(RbacPermissions.ACTIVATION_CREATE) ? RBAC_DISABLED_MESSAGE : null} >
            <span>Create Activation Target</span>
          </Tooltip>
        }
      }}
      searchPlaceholder='Search Targets'
      genericErrorTitle='Error while fetching Targets'
      genericErrorSubTitle='Retry Loading the Targets'
      ListComponent={CardList}
      FiltersComponent={Filters}
      filtersComponentProps={{
        filters: TARGET_FILTERS,
        entity: 'target-list'
      }}
      listComponentProps={{
        CardListItem: TargetListCardItem,
        onCardClick: (target: Target) => {
          navigate(`/target/${target.seqId}/overview`);
        }
      }}
      StandaloneModeActions={CreateTargetButton}
      disableFilters={false}
      pageLimit={50}
      withInfiniteScroll
      ShimmerComponent={CardListShimmer}
      SortComponent={SortDropdown}
      defaultListMeta={{ filters: [], sort: { id: SortDirection.DESC } }}
      sortComponentProps={{
        sortOptions: TARGET_SORT_DROPDOWN_OPTIONS
      }}
    />
  );
}
